import { supabase } from '../lib/supabaseClient';
import { MembershipRequest, MembershipStatus, UserProfile, UserRole } from '../types/profile';

// ─── Member functions (any authenticated user) ───────────────────────────────

/** Submit (or re-submit after denial) a membership request. */
export const applyForMembership = async (userId: string, message: string) => {
  // Upsert on user_id — lets denied users re-apply by overwriting the old row
  const { data, error } = await supabase
    .from('membership_requests')
    .upsert(
      { user_id: userId, message: message.trim(), status: 'pending' },
      { onConflict: 'user_id' },
    )
    .select('*')
    .single<MembershipRequest>();

  return { data, error };
};

/** Get the caller's own membership request (if any). */
export const getMyMembershipRequest = async (userId: string) => {
  const { data, error } = await supabase
    .from('membership_requests')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle<MembershipRequest>();

  return { data, error };
};

/** Withdraw a pending request (user changes their mind). */
export const withdrawMembershipRequest = async (userId: string) => {
  const { error } = await supabase
    .from('membership_requests')
    .delete()
    .eq('user_id', userId)
    .eq('status', 'pending');

  return { error };
};

// ─── Admin functions ──────────────────────────────────────────────────────────

/** Fetch all membership requests with the applicant's profile. Admin only.
 *  Does a two-step fetch because membership_requests.user_id has a FK to
 *  auth.users, not directly to profiles, so Supabase can't auto-join them.
 */
export const getAllMembershipRequests = async () => {
  // Step 1: fetch requests
  const { data: requests, error } = await supabase
    .from('membership_requests')
    .select('*')
    .order('created_at', { ascending: false });

  if (error || !requests || requests.length === 0) {
    return { data: requests as MembershipRequest[] | null, error };
  }

  // Step 2: fetch profiles for those user IDs
  const userIds = requests.map((r) => r.user_id);
  const { data: profileRows } = await supabase
    .from('profiles')
    .select('user_id, username, avatar_url')
    .in('user_id', userIds);

  const profileMap = new Map<string, { username: string; avatar_url: string | null }>(
    (profileRows ?? []).map((p) => [p.user_id, { username: p.username, avatar_url: p.avatar_url }]),
  );

  const merged = requests.map((r) => ({
    ...r,
    profiles: profileMap.get(r.user_id) ?? { username: r.user_id.slice(0, 8), avatar_url: null },
  }));

  return {
    data: merged as (MembershipRequest & {
      profiles: { username: string; avatar_url: string | null };
    })[],
    error: null,
  };
};

/**
 * Approve or deny a request.
 * Approval automatically upgrades the user's role via a DB trigger.
 */
export const updateMembershipStatus = async (
  requestId: string,
  status: MembershipStatus,
  reviewerId: string,
) => {
  const { data, error } = await supabase
    .from('membership_requests')
    .update({ status, reviewed_by: reviewerId, reviewed_at: new Date().toISOString() })
    .eq('id', requestId)
    .select('*')
    .single<MembershipRequest>();

  return { data, error };
};

/** Directly set a user's role. Admin only. */
export const setUserRole = async (userId: string, role: UserRole) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ role })
    .eq('user_id', userId)
    .select('*')
    .single<UserProfile>();

  return { data, error };
};

/** Get all user profiles. Admin only. */
export const getAllProfiles = async () => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .order('created_at', { ascending: false });

  return { data: data as UserProfile[] | null, error };
};
